import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Lightbulb, Sun, FileText, Truck, Landmark, Map, Info, UserCheck } from "lucide-react";

const AssetsTab: React.FC = () => {
    return (
        <div className="px-4 space-y-8 mt-6 pb-24">
            {/* Header section moved into tab content */}
            <div className="pt-2 pb-2">
                <h3 className="text-2xl font-bold leading-tight">Included Assets</h3>
                <p className="text-muted-foreground text-sm font-normal leading-relaxed mt-2">
                    A comprehensive list of all technical and physical assets included in the Quantum Solar project acquisition.
                </p>
            </div>

            {/* Asset Cards */}
            <div className="space-y-4">
                {/* Asset 1: IP */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Verified</Badge>
                                <p className="text-muted-foreground text-xs font-medium">Patent Info</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Intellectual Property</p>
                            <p className="text-muted-foreground text-sm">Grid-tie Inverter Logic</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <Lightbulb className="w-12 h-12 text-primary opacity-50" />
                        </div>
                    </div>
                </Card>

                {/* Asset 2: Physical Prototype */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Verified</Badge>
                                <p className="text-muted-foreground text-xs font-medium">Status: Operational v3.1</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Physical Prototype</p>
                            <p className="text-muted-foreground text-sm">Full-scale 50kW test unit</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <Sun className="w-12 h-12 text-orange-500 opacity-50" />
                        </div>
                    </div>
                </Card>

                {/* Asset 3: Technical Docs */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Verified</Badge>
                                <p className="text-muted-foreground text-xs font-medium">4.2 GB Digital Vault</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Technical Docs</p>
                            <p className="text-muted-foreground text-sm">Blueprints & Schematics</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <FileText className="w-12 h-12 text-blue-500 opacity-50" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Support Period Section */}
            <div className="py-2">
                <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl p-5 flex items-center justify-between">
                    <div>
                        <h4 className="text-primary text-sm font-bold uppercase tracking-widest">Engineering Support</h4>
                        <p className="text-xl font-bold mt-1">06 Months Included</p>
                        <p className="text-muted-foreground text-xs mt-1 italic">Post-acquisition transition period</p>
                    </div>
                    <div className="h-14 w-14 items-center justify-center rounded-full bg-primary text-white flex">
                        <UserCheck className="w-8 h-8" />
                    </div>
                </div>
            </div>

            {/* Transfer Protocol Notes */}
            <div className="py-6">
                <h3 className="text-lg font-bold mb-4">Asset Transfer Notes</h3>
                <div className="space-y-3">
                    {[
                        { icon: Truck, title: "Physical Logistics", desc: "Transfer completed within 30 days of closing" },
                        { icon: Landmark, title: "Digital Rights", desc: "Escrow via official legal channels" },
                        { icon: Map, title: "Land Lease", desc: "Pre-approved site reassignment protocols" }
                    ].map((note, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-card rounded-lg border border-border">
                            <note.icon className="text-primary w-5 h-5 mt-0.5" />
                            <div>
                                <p className="font-bold text-sm">{note.title}</p>
                                <p className="text-xs text-muted-foreground">{note.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AssetsTab;
